import { Link } from 'react-router-dom';
import { FileText, Eye, ThumbsUp, Calendar, ChevronRight } from 'lucide-react';
import { Article } from '@/services/api';
import { formatDistanceToNow } from 'date-fns';

interface ArticleCardProps {
  article: Article;
  showCategory?: boolean;
}

export function ArticleCard({ article, showCategory = true }: ArticleCardProps) {
  const publishedDate = article.publishedAt
    ? formatDistanceToNow(new Date(article.publishedAt), { addSuffix: true })
    : null;

  return (
    <Link
      to={`/article/${article.slug}`}
      className="block bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all group"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
          <FileText className="h-5 w-5 text-gray-600 group-hover:text-primary-600 transition-colors dark:text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{article.excerpt}</p>
          )}

          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
            {showCategory && article.category && (
              <span className="px-2 py-0.5 bg-gray-100 rounded dark:bg-gray-800">
                {article.category.name}
              </span>
            )}
            {publishedDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {publishedDate}
              </span>
            )}
            {article.viewCount > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {article.viewCount}
              </span>
            )}
            {article.helpfulCount > 0 && (
              <span className="flex items-center gap-1">
                <ThumbsUp className="h-3 w-3" />
                {article.helpfulCount}
              </span>
            )}
          </div>

          {/* Tags */}
          {article.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0 dark:text-gray-500" />
      </div>
    </Link>
  );
}
